import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import { api, type ApiResponse } from "@/api/client";
import type { Trip, Destination, Guide } from "@ckgo2/shared/types";
import { ArrowLeft, Save, Loader2, FileText, Map, Image, Calendar } from "lucide-react";
import {
  ItineraryEditor,
  type ItineraryDay,
  GalleryEditor,
  type GalleryImage,
  IncludedListEditor,
  type IncludedItem,
  TripDatesEditor,
  type TripDate,
} from "@/components/trips";
import ImagePicker from "@/components/common/ImagePicker";

interface TripFormData {
  title: string;
  titleSk: string;
  slug: string;
  destinationId: string;
  guideId: string;
  guideVideoUrl: string;
  durationDays: number;
  priceFrom: number;
  shortDescription: string;
  shortDescriptionSk: string;
  description: string;
  descriptionSk: string;
  featuredImage: string;
  difficultyLevel: string;
  tripType: string;
  groupSizeMin: number;
  groupSizeMax: number;
  status: string;
  isFeatured: boolean;
  isLastMinute: boolean;
  // Modular content
  itinerary: ItineraryDay[];
  galleryImages: GalleryImage[];
  included: IncludedItem[];
  excluded: IncludedItem[];
  tags: string[];
  dates: TripDate[];
}

const DIFFICULTIES = [
  { value: "easy", label: "Snadná" },
  { value: "moderate", label: "Střední" },
  { value: "challenging", label: "Náročná" },
  { value: "extreme", label: "Extrémní" },
];

const TABS = [
  { id: "basic", label: "Základní údaje", icon: FileText },
  { id: "itinerary", label: "Itinerář", icon: Map },
  { id: "included", label: "Co je zahrnuto", icon: FileText },
  { id: "gallery", label: "Galerie", icon: Image },
  { id: "dates", label: "Termíny", icon: Calendar },
];

export default function TripEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = id === "new";
  const [activeTab, setActiveTab] = useState("basic");

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<TripFormData>({
    defaultValues: {
      title: "",
      titleSk: "",
      slug: "",
      destinationId: "",
      guideId: "",
      guideVideoUrl: "",
      durationDays: 7,
      priceFrom: 0,
      shortDescription: "",
      shortDescriptionSk: "",
      description: "",
      descriptionSk: "",
      featuredImage: "",
      difficultyLevel: "moderate",
      tripType: "adventure",
      groupSizeMin: 8,
      groupSizeMax: 16,
      status: "draft",
      isFeatured: false,
      isLastMinute: false,
      itinerary: [],
      galleryImages: [],
      included: [],
      excluded: [],
      tags: [],
      dates: [],
    },
  });

  // Fetch trip if editing
  const { data: trip, isLoading } = useQuery({
    queryKey: ["admin-trip", id],
    queryFn: () => api.get<ApiResponse<Trip>>(`/api/admin/trips/${id}`),
    enabled: !isNew,
  });

  // Fetch destinations for dropdown
  const { data: destinationsData } = useQuery({
    queryKey: ["destinations"],
    queryFn: () => api.get<ApiResponse<Destination[]>>("/api/destinations"),
  });

  // Fetch guides for dropdown
  const { data: guidesData } = useQuery({
    queryKey: ["guides"],
    queryFn: () => api.get<ApiResponse<Guide[]>>("/api/guides"),
  });

  const destinations = destinationsData?.data || [];
  const guides = guidesData?.data || [];

  // Reset form when trip data loads
  useEffect(() => {
    if (trip?.data) {
      // Transform API data to form format
      const apiItinerary = trip.data.itinerary || [];
      const apiGallery = trip.data.galleryImages || [];
      const apiIncluded = trip.data.included || [];
      const apiExcluded = trip.data.excluded || [];

      reset({
        title: trip.data.title || "",
        titleSk: trip.data.titleSk || "",
        slug: trip.data.slug || "",
        destinationId: trip.data.destinationId || "",
        guideId: trip.data.guideId || "",
        guideVideoUrl: (trip.data as any).guideVideoUrl || "",
        durationDays: trip.data.durationDays || 7,
        priceFrom: trip.data.priceFrom || 0,
        shortDescription: trip.data.shortDescription || "",
        shortDescriptionSk: trip.data.shortDescriptionSk || "",
        description: trip.data.description || "",
        descriptionSk: trip.data.descriptionSk || "",
        featuredImage: trip.data.featuredImage || "",
        difficultyLevel: trip.data.difficultyLevel || "moderate",
        tripType: trip.data.tripType || "adventure",
        groupSizeMin: trip.data.groupSizeMin || 8,
        groupSizeMax: trip.data.groupSizeMax || 16,
        status: trip.data.status || "draft",
        isFeatured: trip.data.isFeatured || false,
        isLastMinute: trip.data.isLastMinute || false,
        itinerary: apiItinerary.map((day: any) => ({
          day: day.day,
          title: day.title,
          titleSk: day.titleSk || "",
          description: day.description || "",
          descriptionSk: day.descriptionSk || "",
          meals: day.meals || [],
          accommodation: day.accommodation || "",
          highlights: day.highlights || [],
        })),
        galleryImages: apiGallery.map((url: string) => ({
          url,
          alt: "",
          isPrimary: false,
        })),
        included: apiIncluded.map((text: string) => ({ text, textSk: "" })),
        excluded: apiExcluded.map((text: string) => ({ text, textSk: "" })),
        tags: trip.data.tags || [],
        dates: ((trip.data as any).dates || []).map((date: any) => ({
          id: date.id,
          startDate: date.startDate,
          endDate: date.endDate,
          price: date.price,
          capacity: date.capacity,
          bookedCount: date.bookedCount || 0,
          status: date.status,
        })),
      });
    }
  }, [trip, reset]);

  // Auto-generate slug from title
  const title = watch("title");
  useEffect(() => {
    if (isNew && title) {
      const slug = title
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      setValue("slug", slug, { shouldDirty: false });
    }
  }, [title, isNew, setValue]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data: TripFormData) => {
      if (isNew) {
        return api.post<ApiResponse<Trip>>("/api/admin/trips", data);
      }
      return api.put<ApiResponse<Trip>>(`/api/admin/trips/${id}`, data);
    },
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ["trips"] });
      queryClient.invalidateQueries({ queryKey: ["admin-trip", id] });
      if (isNew && response.data?.id) {
        navigate(`/trips/${response.data.id}`);
      }
      // Stay on page after save, reset form dirty state
      reset(variables);
    },
  });

  const onSubmit = async (data: TripFormData) => {
    await saveMutation.mutateAsync(data);
  };

  if (!isNew && isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/trips"
            className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isNew ? "Nový zájezd" : "Upravit zájezd"}
            </h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              {isNew ? "Vytvořte nový zájezd" : `Úprava: ${trip?.data?.title}`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {!isNew && (
            <select
              {...register("status")}
              className="input w-40"
            >
              <option value="draft">Koncept</option>
              <option value="published">Publikovat</option>
              <option value="archived">Archivovat</option>
            </select>
          )}
          <button
            type="button"
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting}
            className="btn-primary"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Ukládám...
              </>
            ) : (
              <>
                <Save className="h-5 w-5" />
                Uložit
              </>
            )}
          </button>
        </div>
      </div>

      {/* Error message */}
      {saveMutation.error && (
        <div className="rounded-lg bg-error-50 p-4 text-error-700 dark:bg-error-900/30 dark:text-error-400">
          {saveMutation.error.message}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex gap-4 overflow-x-auto">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? "border-primary-500 text-primary-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Form */}
      <form
        id="trip-form"
        onSubmit={handleSubmit(onSubmit)}
      >
        {/* Basic Info Tab */}
        {activeTab === "basic" && (
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Main content - 2 columns */}
            <div className="space-y-6 lg:col-span-2">
              {/* Basic info */}
              <div className="card">
                <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                  Základní informace
                </h2>

                <div className="grid gap-4 sm:grid-cols-2">
                  {/* Title CZ */}
                  <div>
                    <label htmlFor="title" className="label">
                      Název (CZ) *
                    </label>
                    <input
                      {...register("title", { required: "Název je povinný" })}
                      type="text"
                      id="title"
                      className={`input ${errors.title ? "input-error" : ""}`}
                      placeholder="např. Bali - Ostrov bohů"
                    />
                    {errors.title && (
                      <p className="mt-1 text-sm text-error-600">
                        {errors.title.message}
                      </p>
                    )}
                  </div>

                  {/* Title SK */}
                  <div>
                    <label htmlFor="titleSk" className="label">
                      Název (SK)
                    </label>
                    <input
                      {...register("titleSk")}
                      type="text"
                      id="titleSk"
                      className="input"
                      placeholder="např. Bali - Ostrov bohov"
                    />
                  </div>

                  {/* Slug */}
                  <div>
                    <label htmlFor="slug" className="label">
                      URL slug *
                    </label>
                    <input
                      {...register("slug", { required: "Slug je povinný" })}
                      type="text"
                      id="slug"
                      className={`input ${errors.slug ? "input-error" : ""}`}
                      placeholder="např. bali-ostrov-bohu"
                    />
                  </div>

                  {/* Destination */}
                  <div>
                    <label htmlFor="destinationId" className="label">
                      Destinace *
                    </label>
                    <select
                      {...register("destinationId", { required: "Destinace je povinná" })}
                      id="destinationId"
                      className={`input ${errors.destinationId ? "input-error" : ""}`}
                    >
                      <option value="">Vyberte destinaci</option>
                      {destinations.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Guide */}
                  <div>
                    <label htmlFor="guideId" className="label">
                      Průvodce
                    </label>
                    <select
                      {...register("guideId")}
                      id="guideId"
                      className="input"
                    >
                      <option value="">Bez průvodce</option>
                      {guides.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Guide Video URL */}
                  <div>
                    <label htmlFor="guideVideoUrl" className="label">
                      Video průvodce (YouTube URL)
                    </label>
                    <input
                      {...register("guideVideoUrl")}
                      type="text"
                      id="guideVideoUrl"
                      className="input"
                      placeholder="https://youtube.com/watch?v=..."
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Video průvodce, kde mluví o tomto zájezdu
                    </p>
                  </div>

                  {/* Difficulty */}
                  <div>
                    <label htmlFor="difficultyLevel" className="label">
                      Náročnost
                    </label>
                    <select
                      {...register("difficultyLevel")}
                      id="difficultyLevel"
                      className="input"
                    >
                      {DIFFICULTIES.map((d) => (
                        <option key={d.value} value={d.value}>
                          {d.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Pricing & Duration */}
              <div className="card">
                <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                  Cena a délka
                </h2>

                <div className="grid gap-4 sm:grid-cols-3">
                  {/* Price From */}
                  <div>
                    <label htmlFor="priceFrom" className="label">
                      Cena od (Kč) *
                    </label>
                    <input
                      {...register("priceFrom", { valueAsNumber: true })}
                      type="number"
                      id="priceFrom"
                      className="input"
                      min={0}
                      step={100}
                    />
                  </div>

                  {/* Duration Days */}
                  <div>
                    <label htmlFor="durationDays" className="label">
                      Délka (dní) *
                    </label>
                    <input
                      {...register("durationDays", { valueAsNumber: true })}
                      type="number"
                      id="durationDays"
                      className="input"
                      min={1}
                    />
                  </div>

                  {/* Group Size */}
                  <div>
                    <label className="label">Velikost skupiny</label>
                    <div className="flex items-center gap-2">
                      <input
                        {...register("groupSizeMin", { valueAsNumber: true })}
                        type="number"
                        className="input w-20"
                        min={1}
                      />
                      <span className="text-gray-500">-</span>
                      <input
                        {...register("groupSizeMax", { valueAsNumber: true })}
                        type="number"
                        className="input w-20"
                        min={1}
                      />
                      <span className="text-sm text-gray-500">osob</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="card">
                <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                  Popis
                </h2>

                <div className="space-y-4">
                  {/* Short Description CZ */}
                  <div>
                    <label htmlFor="shortDescription" className="label">
                      Krátký popis (CZ)
                    </label>
                    <input
                      {...register("shortDescription")}
                      type="text"
                      id="shortDescription"
                      className="input"
                      placeholder="Krátký popisek pro kartu zájezdu"
                    />
                  </div>

                  {/* Short Description SK */}
                  <div>
                    <label htmlFor="shortDescriptionSk" className="label">
                      Krátký popis (SK)
                    </label>
                    <input
                      {...register("shortDescriptionSk")}
                      type="text"
                      id="shortDescriptionSk"
                      className="input"
                      placeholder="Krátky popisok pre kartu zájazdu"
                    />
                  </div>

                  {/* Description CZ */}
                  <div>
                    <label htmlFor="description" className="label">
                      Dlouhý popis (CZ)
                    </label>
                    <textarea
                      {...register("description")}
                      id="description"
                      rows={6}
                      className="input"
                      placeholder="Podrobný popis zájezdu..."
                    />
                  </div>

                  {/* Description SK */}
                  <div>
                    <label htmlFor="descriptionSk" className="label">
                      Dlouhý popis (SK)
                    </label>
                    <textarea
                      {...register("descriptionSk")}
                      id="descriptionSk"
                      rows={6}
                      className="input"
                      placeholder="Podrobný popis zájazdu..."
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar - 1 column */}
            <div className="space-y-6">
              {/* Featured Image */}
              <div className="card">
                <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                  Hlavní obrázek
                </h2>

                <Controller
                  name="featuredImage"
                  control={control}
                  render={({ field }) => (
                    <ImagePicker
                      value={field.value}
                      onChange={field.onChange}
                      folder="trips"
                    />
                  )}
                />
              </div>

              {/* Settings */}
              <div className="card">
                <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                  Nastavení
                </h2>

                <div className="space-y-4">
                  {/* Is Featured */}
                  <div className="flex items-center gap-3">
                    <input
                      {...register("isFeatured")}
                      type="checkbox"
                      id="isFeatured"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <label
                      htmlFor="isFeatured"
                      className="text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Zobrazit jako featured
                    </label>
                  </div>

                  {/* Is Last Minute */}
                  <div className="flex items-center gap-3">
                    <input
                      {...register("isLastMinute")}
                      type="checkbox"
                      id="isLastMinute"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <label
                      htmlFor="isLastMinute"
                      className="text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Last minute nabídka
                    </label>
                  </div>
                </div>
              </div>

              {/* Quick stats */}
              <div className="card bg-gray-50 dark:bg-gray-800/50">
                <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                  Přehled obsahu
                </h2>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Dní v itineráři:</span>
                    <span className="font-medium">{watch("itinerary")?.length || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Obrázků v galerii:</span>
                    <span className="font-medium">{watch("galleryImages")?.length || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Položek "Zahrnuto":</span>
                    <span className="font-medium">{watch("included")?.length || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Položek "Nezahrnuto":</span>
                    <span className="font-medium">{watch("excluded")?.length || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Termínů:</span>
                    <span className="font-medium">{watch("dates")?.length || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Itinerary Tab */}
        {activeTab === "itinerary" && (
          <div className="card">
            <Controller
              name="itinerary"
              control={control}
              render={({ field }) => (
                <ItineraryEditor
                  value={field.value || []}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
        )}

        {/* Included Tab */}
        {activeTab === "included" && (
          <div className="card">
            <Controller
              name="included"
              control={control}
              render={({ field: includedField }) => (
                <Controller
                  name="excluded"
                  control={control}
                  render={({ field: excludedField }) => (
                    <IncludedListEditor
                      included={includedField.value || []}
                      notIncluded={excludedField.value || []}
                      onChangeIncluded={includedField.onChange}
                      onChangeNotIncluded={excludedField.onChange}
                    />
                  )}
                />
              )}
            />
          </div>
        )}

        {/* Gallery Tab */}
        {activeTab === "gallery" && (
          <div className="card">
            <Controller
              name="galleryImages"
              control={control}
              render={({ field }) => (
                <GalleryEditor
                  value={field.value || []}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
        )}

        {/* Dates Tab */}
        {activeTab === "dates" && (
          <div className="card">
            <Controller
              name="dates"
              control={control}
              render={({ field }) => (
                <TripDatesEditor
                  value={field.value || []}
                  onChange={field.onChange}
                  guides={guides.map((g) => ({ id: g.id, name: g.name }))}
                />
              )}
            />
          </div>
        )}
      </form>
    </div>
  );
}
